// Real BPE token counting for the cost estimator, lazy-loaded so the encoding rank
// tables never touch the main bundle. Until the encodings finish loading, callers get
// a chars/4 fallback; a re-render swaps in exact counts once ready.

import { useEffect, useState } from 'react'
import { logError } from 'lib/logging'
import type { Model } from './types'

type Encoding = { countTokens: (text: string) => number }

let o200k: Encoding | null = null
let cl100k: Encoding | null = null
let loadPromise: Promise<void> | null = null

/**
 * OpenAI encoding family for a model id:
 *   cl100k_base → gpt-4 (incl. -turbo), gpt-3.5 / gpt-35
 *   o200k_base  → gpt-4o, gpt-4.1, gpt-5.x, and anything newer/unknown
 */
export function encodingForModel(model: string): 'o200k_base' | 'cl100k_base' {
  if (/^gpt-(4($|-)|3\.5|35)/.test(model)) return `cl100k_base`
  return `o200k_base`
}

/** Lazy-load both encodings once; subsequent calls reuse the same promise. */
export function loadTokenizers(): Promise<void> {
  if (loadPromise) return loadPromise
  loadPromise = Promise.all([import(`gpt-tokenizer/encoding/o200k_base`), import(`gpt-tokenizer/encoding/cl100k_base`)])
    .then(([o200kModule, cl100kModule]) => {
      o200k = { countTokens: o200kModule.countTokens }
      cl100k = { countTokens: cl100kModule.countTokens }
    })
    .catch((error) => logError(`Failed to load gpt-tokenizer encodings`, error))
  return loadPromise
}

export function tokenizersReady(): boolean {
  return o200k !== null && cl100k !== null
}

/** Token count for `text` under `model`'s encoding; chars/4 until loaded. */
export function countTokens(text: string | undefined, model: Model): number {
  const value = text ?? ``
  const encoding = encodingForModel(model) === `cl100k_base` ? cl100k : o200k
  if (!encoding) return Math.ceil(value.length / 4)
  try {
    return encoding.countTokens(value)
  } catch (error) {
    logError(`token count failed; using chars/4 fallback`, error)
    return Math.ceil(value.length / 4)
  }
}

/** Triggers the lazy load and re-renders when the real tokenizer is ready. */
export function useTokenCounter(): { count: typeof countTokens; ready: boolean } {
  const [ready, setReady] = useState(tokenizersReady())
  useEffect(() => {
    if (ready) return
    let active = true
    loadTokenizers().then(() => {
      if (active) setReady(tokenizersReady())
    })
    return () => {
      active = false
    }
  }, [ready])
  return { count: countTokens, ready }
}
